import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, Target, TrendingUp, ArrowRight, CheckCircle2 } from "lucide-react";

const Landing_v2 = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      name: "Essential",
      price: "$9.99",
      coverageAmount: 150,
      features: ["Bills paid fast", "Email support", "Same-day review"]
    },
    {
      name: "Standard",
      price: "$19.99",
      coverageAmount: 300,
      features: ["Priority coverage", "Quick support", "Fast-track review", "Flexible terms"],
      featured: true
    },
    {
      name: "Plus",
      price: "$39.99",
      coverageAmount: 600,
      features: ["Maximum coverage", "24/7 direct line", "Instant review", "Custom terms"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
            <span className="text-xl sm:text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              data-testid="nav-login-btn"
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="rounded-full text-white hover:text-yellow-400 px-3 sm:px-4 text-sm sm:text-base"
            >
              Sign In
            </Button>
            <Button 
              data-testid="nav-signup-btn"
              onClick={() => navigate('/signup')}
              className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-4 py-2 sm:px-8 sm:py-6 text-sm sm:text-lg font-bold"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full mb-6">
              <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wide">
                ⚡ Bills Covered. Right Now.
              </span>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-heading font-black text-white leading-none mb-6 tracking-tight">
              We Pay Your Bills.
              <br />
              <span className="text-yellow-400">You Stay Ahead.</span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-slate-300 mb-12 font-medium max-w-3xl mx-auto">
              Can't make rent this week? Electric about to shut off? We've got you. 
              <span className="text-white font-bold"> No waiting. No runaround. Just done.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/signup')} 
                className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-12 py-8 text-2xl font-black shadow-2xl hover:scale-105 transition-transform"
              >
                Lock It In <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
              <Button 
                data-testid="hero-learn-more-btn"
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-12 py-8 text-2xl font-bold"
                onClick={() => document.getElementById('tiers').scrollIntoView({ behavior: 'smooth' })}
              >
                See Plans
              </Button>
            </div>

            {/* Social Proof */}
            <div className="mt-12 sm:mt-16 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-slate-400">
              <div className="text-center px-4">
                <div className="text-2xl sm:text-3xl font-bold text-white">24/7</div>
                <div className="text-xs sm:text-sm">Always Ready</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-slate-700"></div>
              <div className="text-center px-4">
                <div className="text-2xl sm:text-3xl font-bold text-white">$250K+</div>
                <div className="text-xs sm:text-sm">Bills Covered</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-slate-700"></div>
              <div className="text-center px-4">
                <div className="text-2xl sm:text-3xl font-bold text-white">1000s</div>
                <div className="text-xs sm:text-sm">Members Protected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Stripped Down */}
      <section className="py-20 px-6 bg-black/40">
        <div className="container mx-auto">
          <h2 className="text-5xl md:text-6xl font-heading font-black text-center mb-4 text-white">
            Three Steps. That's It.
          </h2>
          <p className="text-xl text-slate-400 text-center mb-16">No hoops. No waiting games.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 hover:border-yellow-400/50 transition-all">
              <div className="w-16 h-16 bg-yellow-400 text-black rounded-2xl flex items-center justify-center text-3xl font-black mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-heading font-bold mb-3 text-white">Join Up</h3>
              <p className="text-slate-400">Pick your plan. 2 minutes, done.</p>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 hover:border-yellow-400/50 transition-all">
              <div className="w-16 h-16 bg-yellow-400 text-black rounded-2xl flex items-center justify-center text-3xl font-black mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-heading font-bold mb-3 text-white">Hit Us Up</h3>
              <p className="text-slate-400">Bill due? Tell us. We handle it.</p>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 hover:border-yellow-400/50 transition-all">
              <div className="w-16 h-16 bg-yellow-400 text-black rounded-2xl flex items-center justify-center text-3xl font-black mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-heading font-bold mb-3 text-white">Stay Covered</h3>
              <p className="text-slate-400">Lights stay on. Stress stays off.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-5xl md:text-6xl font-heading font-black text-center mb-16 text-white">
            We Cover What <span className="text-yellow-400">Matters</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            {["Rent", "Electric", "Gas", "Water", "Internet", "Phone", "Car Payment", "Insurance", "Essentials"].map((item) => (
              <div key={item} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400 flex-shrink-0" />
                <span className="text-white font-semibold text-sm sm:text-base">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tiers" className="py-24 px-6 bg-black/40">
        <div className="container mx-auto">
          <h2 className="text-5xl md:text-6xl font-heading font-black text-center mb-4 text-white">
            Pick Your Power Level
          </h2>
          <p className="text-xl text-slate-400 text-center mb-16">
            More coverage. More control. More peace.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier) => (
              <div 
                key={tier.name}
                data-testid={`tier-${tier.name.toLowerCase()}`}
                className={`relative overflow-hidden rounded-3xl p-8 transition-all ${
                  tier.featured 
                    ? 'bg-gradient-to-br from-yellow-400 to-orange-500 scale-105 shadow-2xl' 
                    : 'bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 hover:border-yellow-400/50'
                }`}
              >
                {tier.featured && (
                  <div className="absolute top-0 right-0 bg-black text-yellow-400 px-4 py-1 text-sm font-bold rounded-bl-lg">
                    🔥 MOST PICKED
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-heading font-bold mb-2 ${tier.featured ? 'text-black' : 'text-white'}`}>
                    {tier.name}
                  </h3>
                  <div className={`text-6xl font-heading font-black mb-2 ${tier.featured ? 'text-black' : 'text-yellow-400'}`}>
                    {tier.price}
                    <span className="text-2xl opacity-70">/mo</span>
                  </div>
                  <p className={`text-sm ${tier.featured ? 'text-black/70' : 'text-slate-400'}`}>
                    Up to <span className={`font-bold text-lg ${tier.featured ? 'text-black' : 'text-yellow-400'}`}>${tier.coverageAmount}</span> per bill
                  </p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <CheckCircle2 className={`h-5 w-5 mt-0.5 flex-shrink-0 ${tier.featured ? 'text-black' : 'text-yellow-400'}`} />
                      <span className={tier.featured ? 'text-black font-medium' : 'text-white'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  data-testid={`select-${tier.name.toLowerCase()}-btn`}
                  onClick={() => navigate('/signup')}
                  className={tier.featured 
                    ? 'bg-black text-yellow-400 hover:bg-black/90 w-full rounded-full py-6 text-lg font-black' 
                    : 'bg-yellow-400 text-black hover:bg-yellow-300 w-full rounded-full py-6 text-lg font-black'}
                >
                  Lock In {tier.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Aggressive */}
      <section className="py-20 sm:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2djEwaC0xMFYxNmgxMHptMCAxOHYxMGgtMTBWMzRoMTB6TTE4IDE2djEwSDhWMTZoMTB6bTAgMTh2MTBIOFYzNGgxMHptMzYtMzJ2MTBoLTEwVjJoMTB6bTAgMTh2MTBoLTEwVjIwaDEwem0wIDE4djEwaC0xMFYzOGgxMHpNMzYgMnYxMGgtMTBWMmgxMHpNMTggMnYxMEg4VjJoMTB6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10"></div>
        
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-heading font-black mb-6 text-black">
            Stop Stressing.<br />Start Living.
          </h2>
          <p className="text-xl sm:text-2xl text-black/80 font-bold mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
            Your bills don't wait. Neither should you.
          </p>
          <div className="flex justify-center">
            <Button 
              data-testid="footer-cta-btn"
              onClick={() => navigate('/signup')}
              className="bg-black text-yellow-400 hover:bg-black/90 rounded-full px-8 py-6 sm:px-16 sm:py-10 text-xl sm:text-3xl font-black shadow-2xl hover:scale-105 transition-transform"
            >
              Join DueDay Now
            </Button>
          </div>
          <p className="text-black/60 mt-6 sm:mt-8 text-sm font-semibold">
            Takes 2 minutes. Zero BS.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black border-t border-slate-800">
        <div className="container mx-auto text-center">
          <p className="text-slate-400 mb-2">© 2025 DueDay. We got you.</p>
          <p className="text-sm text-slate-500">
            Membership billing via Stripe. Coverage subject to review.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing_v2;
